import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Chip,
  styled,
  Grid,
  Tooltip,
} from "@mui/material";
import {
  CLOSE_DEAL_DONE_TYPE,
  CLOSE_UNSUCCESSFULL_TYPE,
} from "../../../constants/leads";
import { VIEW_COLUMNS } from "../leadsData";
import { LeadTable } from "../LeadTable";
import { selectCurrentagency } from "../../../redux/features/authSlice";
import {
  useEditLeadPoolMutation,
  useGetAgentsQuery,
  useGetSingleAgentLeadsQuery,
  useLeadStatusQuery,
} from "../../../redux/api/leadsApi";
import { FilterList, ViewModule } from "@mui/icons-material";
import * as XLSX from "xlsx";

const ViewLead = () => {
  const currentPath = window.location.pathname;
  const segments = currentPath.split("/");
  const agentId = segments[3];
  const currentAgency = useSelector(selectCurrentagency);

  const [isDialogOpen, setDialogOpen] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);

  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedDealFilter, setSelectedDealFilter] = useState("all");

  const { data: allAgents } = useGetAgentsQuery(currentAgency?.agencyid);
  const { data: STATUS = [] } = useLeadStatusQuery();

  const {
    data: leadsData = [],
    isLoading: LeadsLoading,
    refetch,
  } = useGetSingleAgentLeadsQuery(agentId);

  const [editLeadPool] = useEditLeadPoolMutation();

  // ✅ Same filter logic as AllLead
  const filteredLeads = useMemo(() => {
    let leads = leadsData || [];

    // ✅ If no filter selected, hide successful/unsuccessful
    if (selectedStatus === "all" && selectedDealFilter === "all") {
      leads = leads.filter(
        (lead) =>
          lead.leadStatus !== CLOSE_DEAL_DONE_TYPE &&
          lead.leadStatus !== CLOSE_UNSUCCESSFULL_TYPE
      );
    }

    // ✅ Apply Lead Status filter
    if (selectedStatus !== "all") {
      leads = leads.filter(
        (lead) => Number(lead.leadStatus) === Number(selectedStatus)
      );
    }

    // ✅ Apply Deal Filter
    if (selectedDealFilter === "successful") {
      leads = leads.filter((lead) => lead.leadStatus === CLOSE_DEAL_DONE_TYPE);
    } else if (selectedDealFilter === "unsuccessful") {
      leads = leads.filter(
        (lead) => lead.leadStatus === CLOSE_UNSUCCESSFULL_TYPE
      );
    }

    return leads;
  }, [leadsData, selectedStatus, selectedDealFilter]);

  // ✅ Assigning Logic (same as before)
  const onChangeAction = (e, leads) => {
    if (e.target.value === 0) {
      setDialogOpen(true);
      setSelectedLeads(leads);
    }
  };

  const handleAssign = () => {
    if (selectedAgentId) {
      const leadIds = selectedLeads.map((lead) => lead.leadid);
      const now = new Date();

      const formatted =
        now.getFullYear() +
        "-" +
        String(now.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(now.getDate()).padStart(2, "0") +
        "T" +
        String(now.getHours()).padStart(2, "0") +
        ":" +
        String(now.getMinutes()).padStart(2, "0") +
        ":00";

      const payload = {
        leadIds,
        agentId: selectedAgentId,
        updatedatetime: formatted,
      };

      editLeadPool(payload)
        .unwrap()
        .then(() => {
          refetch();
          setDialogOpen(false);
        })
        .catch((error) => {
          console.error("Error updating leads", error);
        });
    }
  };

  return (
    <Box>
      {/* Header */}
      <StyledHeader>
        <Typography variant="h5" fontWeight={600}>
          Staff Leads
        </Typography>
        <Chip
          label={`${filteredLeads.length} leads`}
          color="primary"
          variant="outlined"
          size="small"
        />
      </StyledHeader>

      {/* ✅ Filters (same as AllLead) */}
      <Box mb={2}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <FilterList color="primary" />
            <h3 style={{ marginLeft: 8, marginBottom: 3 }}>Filters</h3>
          </Box>
        </Box>

        <FilterContent>
          {/* Lead Status Filter */}
          <FormControl
            size="small"
            sx={{
              minWidth: 140,
              ml: 1,
              "& .MuiInputLabel-root": { fontSize: 13 },
              "& .MuiSelect-select": {
                py: 0.5, // reduce vertical padding
                fontSize: 13,
                height: "28px", // smaller height
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
              },
            }}
          >
            <InputLabel>Lead Status</InputLabel>
            <Select
              label="Lead Status"
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
                if (selectedDealFilter !== "all") setSelectedDealFilter("all");
              }}
              MenuProps={{
                disablePortal: true,
                PaperProps: {
                  sx: {
                    mt: 1,
                    fontSize: 13,
                    minWidth: 140,
                  },
                },
                MenuListProps: {
                  sx: {
                    textAlign: "left",
                  },
                },
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
              }}
            >
              <MenuItem value="all">
                <ViewModule fontSize="small" sx={{ mr: 1 }} /> Total Leads
              </MenuItem>
              {STATUS.map((status) => (
                <MenuItem key={status.statusId} value={status.statusId}>
                  {status.statusName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Deal Type Filter */}
          <FormControl
            size="small"
            sx={{
              minWidth: 140,
              "& .MuiInputLabel-root": { fontSize: 13 },
              "& .MuiSelect-select": {
                py: 0.5,
                fontSize: 13,
                height: "28px",
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: "6px",
              },
            }}
          >
            <InputLabel>Deal Status</InputLabel>
            <Select
              label="Deal Status"
              value={selectedDealFilter}
              onChange={(e) => {
                setSelectedDealFilter(e.target.value);
                if (selectedStatus !== "all") setSelectedStatus("all");
              }}
              MenuProps={{
                PaperProps: {
                  sx: { fontSize: 13 },
                },
              }}
            >
              <MenuItem value="all">Closed Leads</MenuItem>
              <MenuItem value="successful">Won Leads</MenuItem>
              <MenuItem value="unsuccessful">Lost Leads</MenuItem>
            </Select>
          </FormControl>
                    <Grid item>
            <Tooltip title="Export filtered leads to CSV">
              <Button
                variant="contained"
                size="small"
                sx={{ color: "white" }}
                onClick={() => {
                  if (!filteredLeads?.length) {
                    alert("No leads data available to export!");
                    return;
                  }

                  // ✅ Step 1: Get visible columns (excluding edit/history/propertydata)
                  let columns = VIEW_COLUMNS().filter(
                    (col) =>
                      col.accessorKey &&
                      !["edit", "history", "propertydata"].includes(
                        col.accessorKey
                      )
                  );

                  // ✅ Step 1.5: Ensure description column is included in export
                  const hasDescription = columns.some(
                    (col) => col.accessorKey === "description"
                  );
                  if (!hasDescription) {
                    columns.push({
                      accessorKey: "description",
                      header: "Description",
                    });
                  }

                  // ✅ Step 2: Build export-ready data
                  const exportData = filteredLeads.map((lead) => {
                    const row = {};
                    columns.forEach((col) => {
                      const key = col.accessorKey;
                      const header = col.header;
                      let value = lead[key];

                      // ✅ Replace status number with readable name
                      if (key === "leadStatus" || key === "status") {
                        value = lead.statusName || "";
                      }

                      // ✅ Include description safely
                      if (key === "description") {
                        value = lead.description || "";
                      }

                      // ✅ Format date fields
                      if (
                        key === "leadTime" ||
                        key === "followupdate" ||
                        key === "leadUpdatedDate"
                      ) {
                        value = value ? new Date(value).toLocaleString() : "";
                      }

                      // ✅ Combine first + last name
                      if (key === "firstname") {
                        value = `${lead.firstname || ""} ${
                          lead.lastname || ""
                        }`.trim();
                      }

                      // ✅ Tag formatting
                      if (key === "leadTag") {
                        value = `L-${lead.leadid}`;
                      }

                      row[header] = value ?? "";
                    });
                    return row;
                  });

                  // ✅ Step 3: Convert to worksheet
                  const worksheet = XLSX.utils.json_to_sheet(exportData);

                  // ✅ Bold headers
                  const range = XLSX.utils.decode_range(worksheet["!ref"]);
                  for (let C = range.s.c; C <= range.e.c; ++C) {
                    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
                    if (!worksheet[cellAddress]) continue;
                    worksheet[cellAddress].s = { font: { bold: true } };
                  }

                  // ✅ Step 4: Export as Excel
                  const workbook = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");
                  XLSX.writeFile(workbook, "LeadsData.xlsx");
                }}
              >
                Export CSV
              </Button>
            </Tooltip>
          </Grid>
        </FilterContent>
      </Box>

      {/* ✅ Table */}
      <LeadTable
        columns={VIEW_COLUMNS(refetch)}
        leads={filteredLeads}
        isLoading={LeadsLoading}
        onChangeAction={onChangeAction}
        applyColor={true}
      />

      {/* ✅ Assign Dialog */}
      <Dialog
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Staff</DialogTitle>
        <DialogContent>
          <RadioGroup
            value={selectedAgentId}
            onChange={(e) => setSelectedAgentId(e.target.value)}
          >
            {allAgents &&
              allAgents
                .filter((agent) => agent.status === 1)
                .map((agent) => (
                  <FormControlLabel
                    key={agent.agentid}
                    value={agent.agentid?.toString()}
                    control={<Radio />}
                    label={agent.agentname}
                  />
                ))}
          </RadioGroup>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAssign} disabled={!selectedAgentId}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViewLead;

const StyledHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(3),
  flexWrap: "wrap",
  gap: theme.spacing(2),
}));
const FilterContent = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "16px", // theme.spacing(2) is usually 8px * 2 = 16px
  flexWrap: "wrap",
});
